import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  getAudienceModeLabel,
  stepDetailSections,
  type AudienceMode,
  type EglyMethodStep,
} from "@/lib/egly-method";
import { getStep06DialogTeaser } from "@/lib/play-injury-free";
import { m } from "@/paraglide/messages.js";

export function MethodStepDialog({
  step,
  audience,
  open,
  onClose,
}: {
  step: EglyMethodStep | null;
  audience: AudienceMode;
  open: boolean;
  onClose: () => void;
}) {
  if (!step) return null;

  const sections = stepDetailSections(step, audience);
  const isInjuryStep = step.n === "06";
  const audienceLabel = getAudienceModeLabel()[audience];
  const step06Teaser = getStep06DialogTeaser();

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-[90vh] gap-0 overflow-y-auto border-white/10 bg-ink p-0 sm:max-w-4xl">
        <DialogHeader className="space-y-4 border-b border-white/10 px-8 pb-6 pt-8 text-start">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-display text-5xl leading-none text-ember">{step.n}</span>
            <span className="bg-ember/15 px-3 py-1 font-display text-xs tracking-[0.16em] text-ember">
              {audienceLabel}
            </span>
          </div>
          <DialogTitle className="font-display text-3xl leading-tight md:text-4xl">
            {step.title}
          </DialogTitle>
          <DialogDescription className="text-base text-foreground/80">
            {step.summary}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-8 px-8 py-8">
          {isInjuryStep ? (
            <div className="space-y-5">
              <p className="text-sm leading-relaxed text-foreground/75 md:text-base">
                {step06Teaser}
              </p>
              <Link
                to="/play-injury-free"
                className="inline-flex items-center gap-2 font-display text-sm tracking-[0.14em] text-ember hover:text-foreground"
                onClick={onClose}
              >
                {m.method_protects_body()} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </Link>
            </div>
          ) : (
            sections.map((section) => (
              <section key={section.heading}>
                <h3 className="font-display text-xl text-foreground">{section.heading}</h3>
                <div className="mt-3 space-y-3">
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph} className="text-sm leading-relaxed text-foreground/75">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </section>
            ))
          )}
        </div>

        <DialogFooter className="border-t border-white/10 px-8 py-6 sm:justify-start">
          <Link to="/schedule" className="btn-ember btn-ember-hover" onClick={onClose}>
            {m.method_dialog_book()} <ArrowRight className="h-5 w-5 rtl:rotate-180" />
          </Link>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
